import { useHead } from '@unhead/vue'
import { SITE_URL } from './useSeo'
import { COMPANY_PHONE } from '@/constants'

type JsonLdData = Record<string, unknown>

export function useJsonLd(data: JsonLdData | JsonLdData[]) {
  const items = Array.isArray(data) ? data : [data]
  useHead({
    script: items.map((item) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(item)
    }))
  })
}

const ORGANIZATION = {
  '@type': 'Organization' as const,
  name: 'IT-Service Harting',
  url: SITE_URL
}

export function useOrganizationJsonLd() {
  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'IT-Service Harting',
      description: 'Cloud infrastructure provider: Managed Kubernetes, web hosting, domains and S3 object storage. EU data centre, DPA under Art. 28 GDPR.',
      url: SITE_URL,
      logo: `${SITE_URL}/media/og-default.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: COMPANY_PHONE.jsonLd,
        email: 'service@itsh.dev',
        contactType: 'customer service'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'IT-Service Harting',
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/products/domains/search?query={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    }
  ])
}

export function useServiceJsonLd(options: { name: string; description: string; url: string }) {
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.name,
    description: options.description,
    url: options.url,
    provider: ORGANIZATION,
    areaServed: 'EU'
  })
}

export function useFaqJsonLd(items: { question: string; answer: string }[]) {
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  })
}

export function useBreadcrumbJsonLd(items: { name: string; url?: string }[]) {
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {})
    }))
  })
}
